import Merge from 'deepmerge';

import Cookie from '@wetransfer/concorde-cookie';

import ActionHelper from '@module/redux-actions';
import Analytics from '@module/analytics';
import Intercom from '@module/intercom';
import Router from '@module/router';
import Translations from '@module/i18n/dictionary';
import {
  HttpResponseCodes,
  ResponseHelpers,
} from '@module/http/axios/response';

import Pending from '@app/action/pending';
import Notification from '@app/action/notification';
import Transfer from '@app/action/transfer';
import Errors from '@app/action/errors';
import Account from '@app/action/account';
import { actions as AccountActions } from '@app/action/account';
import Contacts from '@app/action/contacts';

import API from '@app/api';
import Config from '@app/config';
import Helper from '@app/helper';
import ModelHelper from '@app/helper/model';
import FormDataHelper from '@app/action/helper/formdata';
import { actions as TransferActions } from '@app/action/transfer';
import Transform from '@app/transformers';

import SessionActions from './session';

export const actions = ActionHelper.types([
  'SIGNIN_SUCCESS',
  'SIGNOUT_SUCCESS',

  'GET_USER',

  'CHANGE_USER_LANGUAGE',

  'REQUEST_PASSWORD_RESET_SUCCESS',
  'PASSWORD_RESET_SUCCESS',

  'RESET_USER_STATE',
]);

export default ActionHelper.generate({
  _setCookie() {
    // Remember that this is a plus user
    Cookie.set('wt_plus', 1, { days: 365 });
  },

  _pushSigninAnalytics(dispatch, id, rememberMe = false) {
    // Register some events
    dispatch(SessionActions.acceptTermsConditions()); // NOTE: User accepted this when signing up
    Analytics.pushDataLayerEvent({
      category: 'Plus',
      action: 'Signin',
      label: rememberMe,
      user: {
        user_id: id,
        account_type: 'plus',
        frequency_type: Helper.getUserFrequency(),
      },
    });
  },

  _redirect(state, user, subDomain, route) {
    const { session, transfer } = state;
    let nextPage;

    if (transfer && transfer.items.length > 0) {
      const { id, recipient_id, secret } = transfer;
      let url = `/downloads/${id}`;

      if (recipient_id) {
        url = `${url}/${recipient_id}`;
      }

      nextPage = `${url}/${secret}`;
    } else {
      nextPage = session.redirect || route;
    }

    const url = new URL(
      nextPage,
      `${location.protocol}//${session.domain}:${location.port}`
    );
    const channelUrl = subDomain
      ? new URL(
          nextPage,
          `${location.protocol}//${subDomain}.${session.domain}:${
            location.port
          }`
        )
      : url;

    if (this._canRedirectToChannel(state, user)) {
      location.assign(channelUrl.href);
    } else {
      Router.navigateTo(nextPage);
    }
  },

  _canRedirectToChannel(state, user) {
    const { channel } = state;
    const account = ModelHelper.getAccountFromUser(user);

    // Determine where to go
    const hasChannel = account.subdomain_name && account.active_subscription;
    const alreadyOnChannel =
      hasChannel && channel && channel.name === account.subdomain_name;

    // Redirect to the user channel
    return !!hasChannel && !alreadyOnChannel;
  },

  // If you are logging in and we are not redirecting you with a full page reload,
  // we might want to clear the default recipient (if it's the only one).
  _updateCurrentTransfer(state, dispatch) {
    const { transfer, account, channel } = state;
    if (!account || !channel) {
      return;
    }
    if (!channel.defaultRecipient) {
      return;
    }
    if (transfer.hasStarted) {
      return;
    }
    if (transfer.recipients.length !== 1) {
      return;
    }
    if (transfer.recipients[0] !== channel.defaultRecipient) {
      return;
    }
    dispatch(Transfer.removeRecipient(channel.defaultRecipient));
  },

  _dispatchSignIn(dispatch, user) {
    dispatch({
      type: actions.SIGNIN_SUCCESS,
      details: user,
    });
  },

  _refreshAccount(dispatch, account, subscription) {
    const {
      autorenewal_enabled,
      autorenewal_failed,
      manual_payment_option_available,
      expires_at,
    } = subscription;
    const {
      active_subscription,
      storage_limit,
      transfer_limit,
      use_new_subscription_system,
    } = account;

    dispatch({
      type: AccountActions.REFRESH_ACCOUNT,
      details: {
        id: account.id,
        active_subscription,
        storage_limit,
        transfer_limit,
        subscription: {
          id: subscription.id,
          autorenewal_enabled,
          autorenewal_failed,
          manual_payment_option_available,
          expires_at,
        },
        use_new_subscription_system,
      },
    });
  },

  _dispatchChangeInUserType(dispatch, userType) {
    dispatch({
      type: TransferActions.TRANSFER_CHANGE_USERTYPE,
      userType,
    });
  },

  _dispatchChangeInAccount(dispatch, accountId) {
    dispatch({
      type: TransferActions.TRANSFER_CHANGE_ACCOUNT_ID,
      accountId,
    });
  },

  // Signing up and out actions
  signin(email, password, rememberMe) {
    const pendingId = 'signin';

    return async (dispatch, getState) => {
      // Don't even request if it's empty.
      if (!email || !password) {
        return dispatch(
          Notification.setNotification('error', 'signin_no_data_exception')
        );
      }

      // Pending state
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.createSession({
          email,
          password,
          remember_me: rememberMe,
        });

        // Backwards compatibility
        const { user, subscription, account } = Transform.auth(response.body);

        this._setCookie();
        this._pushSigninAnalytics(dispatch, user.id, rememberMe);
        this._dispatchSignIn(dispatch, user);
        this._refreshAccount(dispatch, account, subscription);
        this._dispatchChangeInUserType(dispatch, 'plus');
        this._dispatchChangeInAccount(dispatch, account.id);

        // Redirect
        const subDomain = account.subdomain_name || null;
        this._redirect(getState(), user, subDomain, '/transfers');

        dispatch(Pending.resetPending(pendingId));
        this._updateCurrentTransfer(getState(), dispatch);
      } catch (error) {
        if (
          ResponseHelpers.isClientError(error.status) &&
          error.body.message === 'invalid_email_or_password'
        ) {
          dispatch(Errors.setError('signin', error.body.message));
          dispatch(
            Notification.setNotification('error', 'signin_invalid_credentials')
          );
        } else {
          dispatch(Errors.setError('signin', error.body.message));
          dispatch(Notification.setNotification('error', 'signin_exception'));
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  signout(redirectTo) {
    const pendingId = 'signout';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.signout();
        // Is there a forward pending? Replace it with a normal transfer.
        const { transfer, account } = getState();
        if (transfer.isForward) {
          dispatch(Transfer.create());
        }
        Analytics.pushDataLayerEvent({
          category: 'Plus',
          action: 'Signout',
          user: {
            user_id: undefined,
            account_type: 'free',
          },
        });

        dispatch({ type: actions.SIGNOUT_SUCCESS });
        this._dispatchChangeInUserType(dispatch, 'free');
        this._dispatchChangeInAccount(dispatch, null);

        // Clean up our store.
        dispatch(Contacts.resetList());
        dispatch(Account.resetAccountState());
        dispatch(this.resetUserState());
        dispatch(Pending.resetPending(pendingId));

        // Shutdown intercom to close session
        Intercom.shutdown();

        // Set sign out cookie with 30 minute expiration
        const expiryDate = new Date(Date.now() + 60 * 30 * 1000);
        Cookie.set(Config.cookies.signout.name, 1, { expires: expiryDate });

        /*
         * Redirect user away from channel if the user is on his own channel
         * If the user is not on a channel, simply redirect to next page
         */
        const { channel } = getState();

        const nextPage = redirectTo
          ? redirectTo
          : Router.getLastRoute('transfer', '/');

        if (
          channel &&
          channel.name &&
          channel.name === account.subdomain_name
        ) {
          const { session } = getState();
          const url = new URL(
            nextPage,
            `${location.protocol}//${session.domain}:${location.port}`
          );
          location.assign(url.href);
        } else {
          Router.navigateTo(nextPage);
        }
      } catch (error) {
        dispatch(Notification.setNotification('error', 'signout_exception'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  signup(params = {}, tracking = {}) {
    const pendingId = 'signup';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      // Remove vat if it only has the prefix
      let data = { ...params };
      if (data.vat_number.length <= 2) {
        data.vat_number = '';
      }

      // Include tracking params
      if (tracking) {
        data = Merge(data, tracking);
      }

      try {
        const response = await API.User.signup(data);

        if (response.body.order_completed && response.body.redirect_to_url) {
          // A real redirect, since the session will be replaced.
          const url = new URL(response.body.redirect_to_url);
          location.assign(url.href);
        }

        // Remember that this is a plus user
        Cookie.set('wt_plus', 1, { days: 365 });
        Cookie.set('wt_sent', 0, { days: 365 });
        Cookie.set('wt_downloaded', 0, { days: 365 });

        // Make sure analytics are enabled (user accepted with checkbox in form)
        if (!Analytics.isEnabled()) {
          Analytics.enable();
        }

        const channel = data.trk || undefined;

        Analytics.pushDataLayerEvent({
          category: 'Plus',
          action: 'Signup',
          label: params['billing'],
          user: {
            user_id: response.body.user.id,
            has_transacted: false,
            subscription_type: params['billing'],
            upload_count: 0,
            download_count: 0,
            frequency_type: Helper.getUserFrequency(),
          },
          channel,
        });

        const { user } = response.body;

        // Direct user to Stripe, otherwise direct to Adyen
        if (user && user.use_new_subscription_system) {
          const { user, subscription, account } = Transform.auth(
            response.body.user
          );

          this._setCookie();
          this._pushSigninAnalytics(dispatch, user.id);
          this._dispatchSignIn(dispatch, user);
          this._refreshAccount(dispatch, account, subscription);
          this._dispatchChangeInUserType(dispatch, 'plus');
          this._dispatchChangeInAccount(dispatch, account.id);

          Router.navigateTo('/payment/methods');
        } else {
          // Get our formdata template and submit it
          const { payment_step } = response.body;
          if (payment_step && payment_step.template) {
            // We're being redirected, so keep the pending state a bit longer.
            FormDataHelper.submit(
              payment_step.template.formdata,
              payment_step.template.form
            );
          }
        }

        setTimeout(
          () => dispatch(Pending.resetPending(pendingId)),
          Config.actions.extended_pending
        );
      } catch (error) {
        if (error && error.body.fields) {
          dispatch(Errors.setError('signup', error.body.fields));
        }
        dispatch(Notification.setNotification('error', 'signup_account_error'));
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  getUser() {
    const pendingId = 'getUser';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.getCurrentUser();

        dispatch({
          type: actions.GET_USER,
          details: response.body,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_user_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updateUser(params = {}) {
    const pendingId = 'account';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const data = { ...params };
        const response = await API.User.updateUser(data);

        dispatch({
          type: actions.GET_USER,
          details: response.body,
        });
        dispatch(Notification.setNotification('notification', 'update_user'));
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          dispatch(Errors.setError('account', error.body.fields));
          dispatch(
            Notification.setNotification('error', 'something_went_wrong')
          );
        } else {
          dispatch(Notification.setNotification('error', 'update_user_error'));
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updateUserLanguage(language) {
    const pendingId = 'updateUserLanguage';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.updateUserLanguage(
          { language },
          {
            pathExtValues: {
              language,
            },
          }
        );

        dispatch({
          type: actions.CHANGE_USER_LANGUAGE,
          language,
        });

        // Update the dictionary
        Translations.update(language, response.body);
      } catch (error) {
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  // Update password action
  updatePassword(params = {}) {
    const pendingId = 'password';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.updatePassword(params);

        dispatch(
          Notification.setNotification('notification', 'update_password')
        );
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          dispatch(Errors.setError('password', error.body.fields));
        } else {
          dispatch(
            Notification.setNotification('error', 'update_password_exception')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  requestPasswordReset(email) {
    const pendingId = 'requestPasswordReset';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.requestPasswordReset({
          email,
        });

        dispatch({
          type: actions.REQUEST_PASSWORD_RESET_SUCCESS,
          details: response.body,
        });
      } catch (error) {
        if (error.status === HttpResponseCodes.NOT_FOUND) {
          dispatch(Errors.setError('requestPasswordReset', error.body.message));
          dispatch(
            Notification.setNotification(
              'error',
              'request_password_reset_no_user'
            )
          );
        } else {
          dispatch(
            Errors.setError(
              'requestPasswordReset',
              'request_password_reset_exception'
            )
          );
          dispatch(
            Notification.setNotification(
              'error',
              'request_password_reset_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  resetPassword(params = {}) {
    const pendingId = 'password';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.resetPassword(params);

        dispatch({
          type: actions.PASSWORD_RESET_SUCCESS,
          details: response.body,
        });

        // Backwards compatibility
        const { user, subscription, account } = Transform.auth(response.body);

        this._setCookie();
        this._pushSigninAnalytics(dispatch, user.id);
        this._dispatchSignIn(dispatch, user);
        this._refreshAccount(dispatch, account, subscription);
        this._dispatchChangeInUserType(dispatch, 'plus');
        this._dispatchChangeInAccount(dispatch, account.id);

        dispatch(Pending.resetPending(pendingId));

        // Redirect
        const subDomain = account.subdomain_name || null;
        this._redirect(getState(), user, subDomain, '/transfers');
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          if (error.body.fields && error.body.fields.reset_password_token) {
            dispatch(
              Notification.setNotification(
                'error',
                'reset_password_token_invalid'
              )
            );
          } else {
            dispatch(
              Notification.setNotification('error', 'reset_password_error')
            );
          }
        } else {
          dispatch(Errors.setError('password', 'reset_password_exception'));
          dispatch(
            Notification.setNotification('error', 'reset_password_exception')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  resetUserState: [actions.RESET_USER_STATE],
});
